import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import React, { useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

type BodyExample = {
  type: "body";
  contentType: "json" | "xml" | "text";
  value: string;
  label?: string;
};

type HeadersExample = {
  type: "headers";
  headers: { key: string; value: string }[];
  label?: string;
};

type ApiRequestExample = BodyExample | HeadersExample;

interface ApiRequestExamplesProps {
  examples: ApiRequestExample[];
  title?: string;
}

const highlightJson = (jsonString: string) => {
  const cleanJson = jsonString.trim();
  return cleanJson
    .replace(/"([^"]+)":/g, '<span style="color: #82bcf7ff">"$1"</span>:')
    .replace(/: "([^"]*)"/g, ': <span style="color: #a3de7cff">"$1"</span>')
    .replace(/: ([0-9]+)/g, ': <span style="color: #fab387">$1</span>')
    .replace(/: (true|false)/g, ': <span style="color: #fab387">$1</span>')
    .replace(/: (null)/g, ': <span style="color: #eb6a8eff">$1</span>')
    .replace(/({|})/g, '<span style="color: #cdd6f4">$1</span>')
    .replace(/(\[|\])/g, '<span style="color: #cdd6f4">$1</span>')
    .replace(/,/g, '<span style="color: #cdd6f4">,</span>');
};

const ApiRequestExamples: React.FC<ApiRequestExamplesProps> = ({
  examples,
  title = "Request Examples",
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (example: ApiRequestExample, index: number) => {
    let textToCopy = "";

    if (example.type === "body") {
      try {
        const parsed = JSON.parse(example.value);
        textToCopy = JSON.stringify(parsed, null, 2);
      } catch {
        textToCopy = example.value;
      }
    } else if (example.type === "headers") {
      textToCopy = example.headers
        .map((h) => `${h.key}: ${h.value}`)
        .join("\n");
    }

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  return (
    <>
      <Typography variant="body2" fontSize={14} fontWeight={600} mb={1}>
        {title}
      </Typography>

      {examples.map((example, index) => {
        return (
          <Box key={index} sx={{ mb: 3 }}>
            {example.label && (
              <Typography
                variant="body2"
                fontSize={12}
                fontWeight={600}
                mb={1}
                sx={{ color: "#6b7280" }}
              >
                {example.label}
              </Typography>
            )}

            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  backgroundColor: "#393942ff",
                  border: "1px solid #45475a",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {/* Header bar */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#2e2e34ff",
                    borderBottom: "1px solid #45475a",
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontSize={12}
                    sx={{
                      color: "#a6adc8",
                      fontWeight: 500,
                    }}
                  >
                    {example.type === "body"
                      ? example.contentType
                      : "headers"}
                  </Typography>

                  <Tooltip
                    title={copiedIndex === index ? "Copied!" : "Copy"}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(example, index)}
                      sx={{
                        color: "#a6adc8",
                        "&:hover": {
                          backgroundColor: "#313244",
                          color: "#cdd6f4",
                        },
                      }}
                    >
                      {copiedIndex === index ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Content area */}
                {example.type === "body" ? (
                  <Box
                    component="pre"
                    sx={{
                      margin: 0,
                      padding: "16px",
                      fontSize: "13px",
                      lineHeight: 1.5,
                      fontFamily:
                        '"Fira Code", "SF Mono", Monaco, Inconsolata, "Roboto Mono", monospace',
                      color: "#cdd6f4",
                      backgroundColor: "#383842ff",
                      overflow: "auto",
                      whiteSpace: "pre",
                      userSelect: "text",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#36363eff",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#45475a",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#585b70",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: highlightJson(example.value),
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      backgroundColor: "#383842ff",
                      fontSize: "13px",
                      fontFamily:
                        '"Fira Code", "SF Mono", Monaco, Inconsolata, "Roboto Mono", monospace',
                      color: "#cdd6f4",
                    }}
                  >
                    {/* Table header */}
                    <Box
                      sx={{
                        display: "flex",
                        borderBottom: "1px solid #45475a",
                        backgroundColor: "#2e2e34ff",
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "center", py: 1 }}>
                        <Typography
                          sx={{
                            color: "#a6adc8",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Key
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "center", py: 1 }}>
                        <Typography
                          sx={{
                            color: "#a6adc8",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Value
                        </Typography>
                      </Box>
                    </Box>

                    {/* Table body */}
                    {example.headers.map((h, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          borderBottom:
                            i !== example.headers.length - 1
                              ? "1px solid #45475a"
                              : "none",
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            textAlign: "center",
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Typography
                            sx={{ color: "#82bcf7ff", fontSize: 13 }}
                          >
                            {h.key}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            textAlign: "center",
                            px: 2,
                            py: 1,
                            wordBreak: "break-all",
                          }}
                        >
                          <Typography
                            sx={{ color: "#a3de7cff", fontSize: 13 }}
                          >
                            {h.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default ApiRequestExamples;
